import { useRobloxAuthentication } from '@rbx/auth';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { useRouter } from 'next/router';
import { ModerationModal } from '@rbx/creator-hub-error';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  CreatorDashboardParameterResults,
  fetchIxpParameters,
  IxpLayers,
} from '@modules/clients/ixpExperiments';

const REACTIVATION_WAIT_TIME = 2000;
const STATUS_MODERATED = 'moderated';
const MODERATION_MODAL_DISABLED_PATH = '/marketplace-policy';

const ModerationWrapper: FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const { status, logout } = useRobloxAuthentication();
  const [isUserModerated, setIsUserModerated] = useState(false);
  const router = useRouter();
  const isModerationModalDisabledForRoute = Boolean(
    router.asPath?.includes(MODERATION_MODAL_DISABLED_PATH),
  );
  const [ixpParameters, setIxpParameters] = useState<CreatorDashboardParameterResults | null>(null);

  useEffect(() => {
    const fetchIxpParametersAsync = async () => {
      const parameters = await fetchIxpParameters(IxpLayers.CreatorDashboard);
      setIxpParameters(parameters);
    };
    fetchIxpParametersAsync();
  }, []);

  useEffect(() => {
    setIsUserModerated(status === STATUS_MODERATED);
  }, [status]);

  const onLogout = useCallback(
    async (setIsLogoutLoading: (isLogoutLoading: boolean) => void) => {
      try {
        await logout();
        router.reload();
      } catch (e) {
        setIsLogoutLoading(false);
        unifiedLoggerClient.logErrorEvent({
          eventName: 'moderationModalLogoutError',
          parameters: {
            error: e instanceof Error ? e.message : 'Unknown error',
          },
        });
      }
    },
    [logout, router],
  );

  const onReactivate = useCallback(() => {
    setTimeout(() => {
      router.reload();
    }, REACTIVATION_WAIT_TIME);
  }, [router]);

  return (
    <React.Fragment>
      {children}
      {ixpParameters?.enableModerationModal &&
        isUserModerated &&
        !isModerationModalDisabledForRoute && (
          <ModerationModal
            onLogout={onLogout}
            onReactivate={onReactivate}
            isOpen={isUserModerated}
            unifiedLoggerClient={unifiedLoggerClient}
          />
        )}
    </React.Fragment>
  );
};

export default ModerationWrapper;
